import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, DollarSign, RefreshCw, Save, AlertCircle, 
  CheckCircle, Loader2, History, Edit2
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { API_URL } from '../utils/apiConfig';

export default function FXRateManager({ token, isOpen, onClose }) {
  const [currentRate, setCurrentRate] = useState(null);
  const [newRate, setNewRate] = useState('');
  const [newSpread, setNewSpread] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      fetchCurrentRate();
      fetchHistory();
    }
  }, [isOpen]);
  
  const fetchCurrentRate = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/fx/current-rate`);
      if (response.ok) {
        const data = await response.json();
        setCurrentRate(data);
        setNewRate(data.reference_rate?.toString() || '');
        setNewSpread(data.spread?.toString() || '2');
      }
    } catch (error) {
      console.error('Failed to fetch FX rate:', error);
      toast.error('Failed to load current rate');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/fx/rates?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data.rates || []);
      }
    } catch (error) {
      console.error('Failed to fetch FX history:', error);
    }
  };
  
  const handleUpdateRate = async () => {
    if (!newRate || !reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      // Create new FX rate
      const response = await fetch(`${API_URL}/api/admin/fx/rates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currency_from: 'USD',
          currency_to: 'DOP',
          reference_rate: parseFloat(newRate),
          spread: parseFloat(newSpread) || 2.0,
          effective_from: new Date().toISOString(),
          notes: reason
        })
      });
      
      if (response.ok) {
        toast.success('FX rate updated successfully');
        setReason('');
        fetchCurrentRate();
        fetchHistory();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update rate');
      }
    } catch (error) {
      console.error('Failed to update FX rate:', error);
      toast.error('Failed to update rate');
    } finally {
      setSaving(false);
    }
  };
  
  const handleQuickSpreadUpdate = async () => {
    if (!newSpread || !reason) {
      toast.error('Please provide a reason for the spread change');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/fx/quick-spread`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          new_spread: parseFloat(newSpread),
          reason: reason
        })
      });
      
      if (response.ok) {
        toast.success('Spread updated successfully');
        setReason('');
        fetchCurrentRate();
        fetchHistory();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update spread');
      }
    } catch (error) {
      console.error('Failed to update spread:', error);
      toast.error('Failed to update spread');
    } finally {
      setSaving(false);
    }
  };
  
  if (!isOpen) return null;
  
  const appliedRate = currentRate ? (parseFloat(newRate) - parseFloat(newSpread || 0)) : 0;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            FX Rate Management
          </DialogTitle>
          <DialogDescription>
            Manage USD to DOP exchange rates. Changes apply to new transactions only.
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Current Rate Display */}
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-emerald-700">Current Applied Rate</span>
                <span className="text-xs text-emerald-600 bg-emerald-100 px-2 py-1 rounded-full">
                  {currentRate?.source || 'system'}
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-emerald-800">
                  {currentRate?.rate?.toFixed(2) || '60.00'}
                </span>
                <span className="text-lg text-emerald-600">DOP per USD</span>
              </div>
              <div className="mt-2 flex items-center gap-4 text-sm text-emerald-700">
                <span>Reference: {currentRate?.reference_rate?.toFixed(2) || '-'}</span>
                <span>•</span>
                <span>Spread: {currentRate?.spread?.toFixed(2) || '2.00'}</span>
              </div>
              {currentRate?.last_updated && (
                <p className="text-xs text-emerald-600 mt-2">
                  Last updated: {new Date(currentRate.last_updated).toLocaleString()}
                </p>
              )}
            </div>
            
            {/* Rate Update Form */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Edit2 className="w-4 h-4" />
                Update Rate
              </h3>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <Label htmlFor="reference-rate">Reference Rate (DOP)</Label>
                  <Input
                    id="reference-rate"
                    type="number"
                    step="0.01"
                    value={newRate}
                    onChange={(e) => setNewRate(e.target.value)}
                    placeholder="62.00"
                    data-testid="fx-reference-rate"
                  />
                  <p className="text-xs text-slate-500 mt-1">Market/interbank rate</p>
                </div>
                <div>
                  <Label htmlFor="spread">Spread (DOP)</Label>
                  <Input
                    id="spread"
                    type="number"
                    step="0.01"
                    value={newSpread}
                    onChange={(e) => setNewSpread(e.target.value)}
                    placeholder="2.00"
                    data-testid="fx-spread"
                  />
                  <p className="text-xs text-slate-500 mt-1">Your margin</p>
                </div>
              </div>
              
              {/* Preview */}
              <div className="bg-slate-50 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Applied Rate Preview:</span>
                  <span className="text-lg font-bold text-slate-900">
                    {appliedRate.toFixed(2)} DOP/USD
                  </span>
                </div>
              </div>
              
              <div className="mb-4">
                <Label htmlFor="reason">Reason for Change *</Label>
                <Input
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Market adjustment, Weekly update"
                  data-testid="fx-reason"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={handleUpdateRate}
                  disabled={saving || !newRate || !reason}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="fx-update-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Update Full Rate
                </Button>
                <Button
                  onClick={handleQuickSpreadUpdate}
                  disabled={saving || !newSpread || !reason}
                  variant="outline"
                  className="flex-1"
                  data-testid="fx-spread-btn"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Update Spread Only
                </Button>
              </div>
            </div>
            
            {/* Rate History */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Recent Rate Changes
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchHistory}
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              
              {history.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {history.map((rate, i) => (
                    <div
                      key={rate.id || i}
                      className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0"
                    >
                      <div>
                        <span className="font-medium text-slate-900">
                          {rate.applied_rate?.toFixed(2)} DOP/USD
                        </span>
                        <p className="text-xs text-slate-500">
                          {rate.notes || 'No reason provided'}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          rate.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                          rate.status === 'superseded' ? 'bg-slate-100 text-slate-600' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {rate.status}
                        </span>
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(rate.effective_from).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-4">No rate history available</p>
              )}
            </div>
            
            {/* Warning */}
            <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Important</p>
                <p className="text-xs text-amber-700">
                  Rate changes only affect new authorizations. Existing authorizations use their original rate.
                </p>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
